import { Recommendation } from '@/hooks/useRecommendations';

export const DEMO_RECOMMENDATIONS: Recommendation[] = [
  {
    id: 'demo-1',
    supplementId: 'demo-1',
    name: 'Magnesium Glycinate',
    description: 'A highly bioavailable form of magnesium bound to the amino acid glycine.',
    full_description: 'Magnesium Glycinate is one of the most bioavailable and well-tolerated forms of magnesium. It is chelated to the amino acid glycine, which itself has calming properties. This form is less likely to cause digestive discomfort compared to other magnesium supplements.',
    priority: 'high',
    reason: 'Based on your stress levels and fitness goals, magnesium glycinate can support muscle recovery, sleep quality, and overall nervous system function.',
    notes: 'Best taken in the evening due to its calming effects. Can be taken with or without food.',
    tags: ['sleep', 'stress', 'recovery'],
    dosage: '400mg daily',
    dosage_male: '400–420mg elemental magnesium daily',
    dosage_female: '310–360mg elemental magnesium daily',
    how_to_take: 'Take 1-2 capsules with water in the evening, 30-60 minutes before bed. Can be split into two doses if preferred.',
    how_to_take_simple: '1-2 capsules before bed',
    precautions: 'May interact with antibiotics and blood pressure medications. Consult your doctor if you have kidney disease.',
    type: 'clinical',
    rating: 4.8,
    benefits: ['Sleep Quality', 'Stress Relief', 'Muscle Recovery', 'Nervous System Support'],
    benefitsLong: {
      'Sleep Quality': 'Magnesium activates the parasympathetic nervous system, helping you relax and fall asleep more easily. It also regulates melatonin production.',
      'Stress Relief': 'Magnesium plays a key role in regulating the HPA axis, your body\'s central stress response system, helping to lower cortisol levels.',
      'Muscle Recovery': 'Essential for proper muscle contraction and relaxation. Helps prevent cramps and supports post-exercise recovery.',
      'Nervous System Support': 'Acts as a natural calcium channel blocker, helping to calm neural activity and support overall nervous system health.'
    },
    matchConfidence: 0.95,
    matchesGoals: ['sleep', 'fitness']
  },
  {
    id: 'demo-2',
    supplementId: 'demo-2',
    name: 'Omega-3 Fish Oil',
    description: 'High-quality fish oil providing EPA and DHA essential fatty acids.',
    full_description: 'Omega-3 fatty acids are essential fats that your body cannot produce on its own. EPA (eicosapentaenoic acid) and DHA (docosahexaenoic acid) are the two most important forms found in fish oil, supporting cardiovascular, brain, and joint health.',
    priority: 'high',
    reason: 'Your dietary profile suggests limited seafood intake. Omega-3 supplementation can fill this nutritional gap and support your cognitive and cardiovascular health goals.',
    notes: 'Look for products tested for heavy metals and oxidation. Triglyceride form is better absorbed than ethyl ester.',
    tags: ['heart', 'brain', 'inflammation'],
    dosage: '1000-2000mg combined EPA/DHA daily',
    dosage_male: '1000–2000mg combined EPA/DHA daily',
    dosage_female: '1000–2000mg combined EPA/DHA daily',
    how_to_take: 'Take with a meal containing fat for optimal absorption. Can be split into morning and evening doses.',
    how_to_take_simple: '1-2 softgels with meals',
    precautions: 'May increase bleeding risk. Consult your doctor if taking blood thinners or before surgery.',
    type: 'clinical',
    rating: 4.7,
    benefits: ['Heart Health', 'Brain Function', 'Joint Support', 'Mood Regulation'],
    benefitsLong: {
      'Heart Health': 'EPA and DHA help reduce triglycerides, lower blood pressure, and decrease the risk of heart arrhythmias, supporting overall cardiovascular health.',
      'Brain Function': 'DHA makes up a significant portion of brain cell membranes. Adequate intake supports memory, focus, and cognitive processing speed.',
      'Joint Support': 'Omega-3s have potent anti-inflammatory properties that can reduce joint stiffness and pain, particularly beneficial for active individuals.',
      'Mood Regulation': 'EPA in particular has been shown to support healthy mood balance by modulating inflammatory pathways linked to mood disorders.'
    },
    matchConfidence: 0.92,
    matchesGoals: ['general_wellbeing', 'mental_clarity']
  },
  {
    id: 'demo-3',
    supplementId: 'demo-3',
    name: 'Vitamin D3',
    description: 'The most bioactive form of vitamin D, essential for bone health and immune function.',
    full_description: 'Vitamin D3 (cholecalciferol) is the form of vitamin D naturally produced by your skin in response to sunlight. It is crucial for calcium absorption, bone health, immune function, and mood regulation. Deficiency is extremely common, especially in northern climates.',
    priority: 'high',
    reason: 'Vitamin D deficiency is widespread and impacts energy, immunity, and mood — all areas aligned with your health goals.',
    notes: 'Best absorbed when taken with a fat-containing meal. Consider pairing with Vitamin K2 for optimal calcium metabolism.',
    tags: ['immune', 'bones', 'energy'],
    dosage: '2000-4000 IU daily',
    dosage_male: '2000–4000 IU daily (50–100mcg)',
    dosage_female: '2000–4000 IU daily (50–100mcg)',
    how_to_take: 'Take one softgel or drop with your largest meal of the day for best absorption.',
    how_to_take_simple: '1 softgel with your main meal',
    precautions: 'High doses over extended periods can cause calcium buildup. Have your levels tested periodically.',
    type: 'clinical',
    rating: 4.9,
    benefits: ['Immune Support', 'Bone Health', 'Energy Levels', 'Mood Support'],
    benefitsLong: {
      'Immune Support': 'Vitamin D activates key immune cells including T-cells and macrophages. Adequate levels are associated with reduced risk of respiratory infections.',
      'Bone Health': 'Essential for calcium absorption in the gut. Without sufficient vitamin D, bones can become thin, brittle, or misshapen.',
      'Energy Levels': 'Vitamin D receptors are found in muscle tissue. Deficiency is strongly linked to fatigue, and supplementation often improves energy in deficient individuals.',
      'Mood Support': 'Vitamin D receptors are present throughout the brain. Low levels are associated with seasonal mood changes and supplementation may help maintain positive mood.'
    },
    matchConfidence: 0.90,
    matchesGoals: ['immune_support', 'energy']
  },
  {
    id: 'demo-4',
    supplementId: 'demo-4',
    name: 'Ashwagandha',
    description: 'An adaptogenic herb traditionally used in Ayurvedic medicine for stress and vitality.',
    full_description: 'Ashwagandha (Withania somnifera) is a powerful adaptogen that has been used for over 3,000 years in Ayurvedic medicine. Modern research supports its ability to reduce cortisol levels, improve stress resilience, and enhance physical performance.',
    priority: 'medium',
    reason: 'Your reported stress levels suggest an adaptogen could help regulate your cortisol response and support both mental and physical performance.',
    notes: 'KSM-66 and Sensoril are the most clinically studied extracts. Effects typically build over 4-8 weeks of consistent use.',
    tags: ['stress', 'adaptogen', 'performance'],
    dosage: '300-600mg daily',
    dosage_male: '600mg standardized extract daily (KSM-66)',
    dosage_female: '300–600mg standardized extract daily (KSM-66)',
    how_to_take: 'Take with breakfast or split into morning and evening doses. Consistent daily use is key for best results.',
    how_to_take_simple: '1 capsule with breakfast',
    precautions: 'Not recommended during pregnancy. May interact with thyroid medications and immunosuppressants.',
    type: 'herbal',
    rating: 4.6,
    benefits: ['Stress Reduction', 'Physical Performance', 'Mental Clarity', 'Sleep Quality'],
    benefitsLong: {
      'Stress Reduction': 'Clinical trials show ashwagandha can reduce cortisol levels by up to 30%, significantly improving perceived stress and anxiety levels.',
      'Physical Performance': 'Studies demonstrate improvements in VO2 max, muscle strength, and recovery time in both trained and untrained individuals.',
      'Mental Clarity': 'Ashwagandha supports cognitive function by reducing the negative effects of chronic stress on memory and attention.',
      'Sleep Quality': 'The GABAergic activity of ashwagandha\'s active compounds (withanolides) promotes relaxation and helps improve sleep onset and quality.'
    },
    matchConfidence: 0.85,
    matchesGoals: ['mood', 'fitness']
  },
  {
    id: 'demo-5',
    supplementId: 'demo-5',
    name: 'Creatine Monohydrate',
    description: 'The most researched sports supplement, supporting strength, power, and cognitive function.',
    full_description: 'Creatine monohydrate is the most extensively studied and effective sports supplement available. It increases phosphocreatine stores in muscles, allowing for greater ATP regeneration during high-intensity exercise. Emerging research also supports cognitive benefits.',
    priority: 'medium',
    reason: 'Given your exercise routine, creatine can enhance your training performance and recovery while also supporting cognitive function.',
    notes: 'Monohydrate is the gold standard — no need for fancy forms. Micronized versions mix more easily.',
    tags: ['fitness', 'strength', 'cognitive'],
    dosage: '3-5g daily',
    dosage_male: '5g daily',
    dosage_female: '3–5g daily',
    how_to_take: 'Mix 5g into water, juice, or a protein shake. Timing doesn\'t matter — consistency is what counts. No loading phase necessary.',
    how_to_take_simple: '1 scoop (5g) mixed into any drink daily',
    precautions: 'Stay well hydrated. Safe for long-term use in healthy individuals. Consult your doctor if you have kidney concerns.',
    type: 'clinical',
    rating: 4.9,
    benefits: ['Strength & Power', 'Muscle Recovery', 'Cognitive Function', 'Exercise Capacity'],
    benefitsLong: {
      'Strength & Power': 'Creatine increases phosphocreatine availability, allowing muscles to produce more ATP during short bursts of intense activity like lifting or sprinting.',
      'Muscle Recovery': 'By enhancing cellular energy availability, creatine helps reduce muscle damage markers and inflammation following intense training.',
      'Cognitive Function': 'The brain is highly metabolically active and benefits from increased ATP availability. Studies show creatine can improve short-term memory and reasoning.',
      'Exercise Capacity': 'Meta-analyses show consistent improvements of 5-15% in high-intensity exercise performance with regular creatine supplementation.'
    },
    matchConfidence: 0.82,
    matchesGoals: ['fitness', 'energy']
  },
  {
    id: 'demo-6',
    supplementId: 'demo-6',
    name: 'Rhodiola Rosea',
    description: 'An adaptogenic herb that combats fatigue and enhances mental performance under stress.',
    full_description: 'Rhodiola Rosea is an adaptogenic herb native to cold, mountainous regions of Europe and Asia. It has a long history of use for combating fatigue, enhancing mental performance, and improving stress tolerance. It works primarily by modulating cortisol and supporting neurotransmitter balance.',
    priority: 'low',
    reason: 'As a complementary adaptogen, rhodiola can provide additional support for energy and mental clarity, especially during demanding periods.',
    notes: 'Best taken in the morning or early afternoon as it can be mildly stimulating. Look for extracts standardized to 3% rosavins and 1% salidroside.',
    tags: ['energy', 'focus', 'adaptogen'],
    dosage: '200-400mg daily',
    dosage_male: '400mg standardized extract daily',
    dosage_female: '200–400mg standardized extract daily',
    how_to_take: 'Take in the morning on an empty stomach, 15-30 minutes before breakfast. Avoid taking late in the day.',
    how_to_take_simple: '1 capsule in the morning before breakfast',
    precautions: 'May cause mild stimulation in sensitive individuals. Avoid combining with prescription stimulants.',
    type: 'herbal',
    rating: 4.4,
    benefits: ['Mental Performance', 'Energy & Stamina', 'Stress Resilience', 'Focus & Attention'],
    benefitsLong: {
      'Mental Performance': 'Rhodiola has been shown to improve cognitive function during prolonged periods of stress, including better accuracy and faster processing speed.',
      'Energy & Stamina': 'By optimizing mitochondrial energy production and oxygen utilization, rhodiola helps combat both physical and mental fatigue.',
      'Stress Resilience': 'As an adaptogen, rhodiola helps normalize the stress response by modulating cortisol release and supporting adrenal function.',
      'Focus & Attention': 'Rhodiola supports dopamine and norepinephrine signaling, key neurotransmitters involved in sustained attention and motivation.'
    },
    matchConfidence: 0.75,
    matchesGoals: ['energy', 'mental_clarity']
  }
];
